import { useState, useEffect, useRef } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

type HeroCarouselProps = {
  images: string[];
  className?: string;
  imagePosition?: string;
  mobileImagePositions?: (number | null)[];
};

export default function HeroCarousel({ images, className, imagePosition = "object-center", mobileImagePositions }: HeroCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 480);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 480);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (!images || images.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images]);

  if (!images || images.length === 0) {
    return null;
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current;
      const swipeThreshold = 50; // Minimum swipe distance
      if (diff > swipeThreshold) {
        nextSlide(); // Swipe left → next
      } else if (diff < -swipeThreshold) {
        prevSlide(); // Swipe right → prev
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div
      className={`relative w-full overflow-hidden touch-pan-y ${className ?? "h-80 sm:h-[390px]"}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {images.map((img, index) => {
        const mobilePos = mobileImagePositions?.[index];
        const mobileStyle = isMobile && mobilePos != null
          ? { objectPosition: `${mobilePos}% center` }
          : undefined;
        return (
          <img
            key={index}
            src={img}
            alt={`Slide ${index}`}
            className={`absolute top-0 left-0 w-full h-full object-cover ${imagePosition} transition-opacity duration-500 ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
            style={mobileStyle}
          />
        );
      })}

      {images.length > 1 && (
        <>
          <div className="absolute bottom-4 w-full flex justify-center gap-2">
            {images.map((_, index) => (
              <button
                key={index}
                className={`w-2.5 h-2.5 rounded-full border border-white/60 transition-colors ${
                  index === currentIndex ? "bg-white" : "bg-black/30"
                }`}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </div>

          <button
            onClick={prevSlide}
            className="hidden sm:flex sm:absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/30 hover:bg-black/50 rounded-full w-9 h-9 items-center justify-center transition-colors"
          >
            <FaChevronLeft className="text-sm" />
          </button>

          <button
            onClick={nextSlide}
            className="hidden sm:flex sm:absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/30 hover:bg-black/50 rounded-full w-9 h-9 items-center justify-center transition-colors"
          >
            <FaChevronRight className="text-sm" />
          </button>
        </>
      )}
    </div>
  );
};
